import { Marked } from 'marked';
import DOMPurify from 'dompurify';

// A scoped instance (not the shared default export) so configuring gfm/breaks
// here can't affect other `marked` call sites in the app.
const marked = new Marked({ gfm: true, breaks: true });

// Models sometimes use a bold-only line ("**By Courier**") as a section
// label instead of an ATX heading. Negative lookahead/char-class guard
// against ***text*** (bold wrapping italic) matching here instead of as
// plain nested emphasis.
const BOLD_ONLY_LINE_RE = /^\*\*(?!\*)(.+[^*])\*\*$/;
// A bold-only line immediately adjacent (no blank line) to a list item is
// almost always an aside *within* that list, not a real section break --
// promoting it to a heading there splits one continuous list into two
// separate <ul>/<ol> blocks around an unrelated heading.
const LIST_MARKER_RE = /^\s*(?:[-*]\s+|\d+[.)]\s+)/;

const ALLOWED_TAGS = [
  'p',
  'strong',
  'em',
  'br',
  'a',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'table',
  'thead',
  'tbody',
  'tr',
  'th',
  'td',
  'ul',
  'ol',
  'li',
  'hr',
];
const ALLOWED_ATTR = ['href', 'target', 'rel', 'start'];

/**
 * Chat-message markdown -> sanitized HTML. Parses with `marked` (a
 * spec-compliant GFM parser — real nested lists, tables, headings) instead
 * of hand-rolled regex, then sanitizes with DOMPurify against a tight
 * allowlist.
 */
export function formatChatMessageHtml(text: string): string {
  // Promote a bold-only line to a heading before parsing, so it gets
  // heading spacing/weight (h4, subordinate to a real ATX heading) instead
  // of rendering as an inline-bold prose line glued to what follows -- but
  // not when it sits directly next to a list item (see LIST_MARKER_RE).
  const lines = text.split('\n');
  const normalized = lines
    .map((line, i) => {
      const m = line.trim().match(BOLD_ONLY_LINE_RE);
      if (!m) return line;
      const prevIsListItem = i > 0 && LIST_MARKER_RE.test(lines[i - 1]);
      const nextIsListItem =
        i < lines.length - 1 && LIST_MARKER_RE.test(lines[i + 1]);
      return prevIsListItem || nextIsListItem ? line : `#### ${m[1]}`;
    })
    .join('\n');

  const html = marked.parse(normalized, { async: false }) as string;

  // Add target="_blank"/rel="noopener noreferrer" to every link by walking
  // the sanitized DOM fragment directly, rather than DOMPurify's addHook
  // (which mutates the shared global DOMPurify instance and would leak this
  // behavior into every unrelated DOMPurify.sanitize() call elsewhere in
  // the app). RETURN_DOM_FRAGMENT keeps this entirely local to this call.
  const fragment = DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    RETURN_DOM_FRAGMENT: true,
  });
  fragment.querySelectorAll('a').forEach((a) => {
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
  });
  const container = document.createElement('div');
  container.appendChild(fragment);
  return container.innerHTML;
}
